import {
    ArrowLeftIcon,
    AwardIcon,
    BriefcaseIcon,
    Building2Icon,
    ClockIcon,
    ReceiptIcon,
    SvgIconComponent,
    TrendingUpIcon,
    UserIcon,
    UsersIcon,
} from 'components/icons';
import { useEffect, useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useReducedMotion } from 'hooks/useReducedMotion';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { cn } from 'utils/cn';
import { FamilyId, familyTileClass } from 'utils/families';

/**
 * Texts reveal, for a view people open now and then: each part rises out of a blur as it first draws, 500ms, from
 * `@starting-style`, so nothing waits on a script. Under reduced motion it only fades, quickly. A later part adds its
 * delay, 80ms a step.
 */
export const TEXTS_REVEAL =
    'transition-[opacity,translate,filter] duration-(--duration-very-slow) ease-(--ease-smooth-out) starting:opacity-0 motion-safe:starting:translate-y-(--distance-medium) motion-safe:starting:blur-(--blur-medium) motion-reduce:delay-0 motion-reduce:duration-(--duration-quick)';

/** What an empty state shows: its prompts, What it can do, or its prompts again with focus back on the link. */
export type CapabilitiesView = 'prompts' | 'capabilities' | 'returned';

/** Texts reveal's exit: the leaving view fades as one, in place, before the next one mounts and rises in. */
const VIEW_LEAVE =
    'pointer-events-none opacity-0 transition-opacity duration-200 ease-[ease] motion-reduce:duration-(--duration-quick)';

/**
 * The empty state's view. `show` holds the current view on screen for its fade (200ms, 150ms under reduced
 * motion) and then swaps; `leaveClassName` goes on the root of whichever view is drawn.
 */
export const useCapabilitiesView = () => {
    const [view, setView] = useState<CapabilitiesView>('prompts');
    const [next, setNext] = useState<CapabilitiesView | null>(null);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        if (next === null) {
            return undefined;
        }
        const timer = setTimeout(
            () => {
                setView(next);
                setNext(null);
            },
            reducedMotion ? 150 : 200
        );
        return () => clearTimeout(timer);
    }, [next, reducedMotion]);

    return { view, show: setNext, leaveClassName: next === null ? undefined : VIEW_LEAVE };
};

// The records Crespect AI reads, each on its family's tile. Only what it covers: nothing it does not.
const COVERED: Array<[string, SvgIconComponent, FamilyId?]> = [
    ['clients', UsersIcon, 'contacts'],
    ['cases', BriefcaseIcon, 'cases'],
    ['timecards', ClockIcon],
    ['bd-efforts', TrendingUpIcon, 'bd'],
    ['invoices', ReceiptIcon, 'finance'],
    ['colleagues', UserIcon, 'people'],
    ['offices', Building2Icon, 'people'],
    ['experience-records', AwardIcon, 'bd'],
];

/** The empty state's way into What it can do. Focused again when the user comes back from it. */
export const AiChatCapabilitiesLink: React.FC<{ onClick: () => void; autoFocus?: boolean }> = ({
    onClick,
    autoFocus,
}) => (
    <button
        type="button"
        onClick={onClick}
        // Mounted again on the way back, so this hands focus back to what opened the view.
        autoFocus={autoFocus}
        className={cn(
            'text-caption text-foreground hover:text-link hit-area relative shrink-0 cursor-pointer font-medium underline',
            FOCUS_RING
        )}
    >
        <FormattedMessage id="ai-chat.capabilities.open" />
    </button>
);

/** What it can do: the data Crespect AI covers, in the empty state's place, with a way back to the prompts. */
const AiChatCapabilities: React.FC<{ onBack: () => void; className?: string }> = ({ onBack, className }) => {
    const coveredId = useId();

    return (
        <div className={cn('flex flex-col gap-6', className)}>
            <header className={cn('flex flex-col items-start gap-1', TEXTS_REVEAL)}>
                <button
                    type="button"
                    onClick={onBack}
                    autoFocus
                    className={cn(
                        'text-meta text-foreground-secondary hover:text-foreground hit-area relative inline-flex cursor-pointer items-center gap-1 font-medium transition-colors',
                        FOCUS_RING
                    )}
                >
                    <ArrowLeftIcon className="size-4 rtl:-scale-x-100" />
                    <FormattedMessage id="ai-chat.capabilities.back" />
                </button>
                <h2 className="text-dialog-title text-foreground text-balance">
                    <FormattedMessage id="ai-chat.capabilities.title" />
                </h2>
            </header>
            <section
                aria-labelledby={coveredId}
                className={cn('@container flex flex-col gap-2', TEXTS_REVEAL, 'delay-(--duration-micro)')}
            >
                <h3 id={coveredId} className="text-label text-muted-foreground uppercase">
                    <FormattedMessage id="ai-chat.capabilities.covered" />
                </h3>
                <ul className="grid grid-cols-4 gap-x-2 gap-y-3">
                    {COVERED.map(([id, Icon, family]) => (
                        <li
                            key={id}
                            className="flex flex-col items-center gap-1.5 text-center @xl:flex-row @xl:gap-2.5 @xl:text-start"
                        >
                            <span
                                className={cn(
                                    'grid size-9 shrink-0 place-items-center rounded-[10px]',
                                    familyTileClass(family)
                                )}
                            >
                                <Icon className="size-[18px]" />
                            </span>
                            <span className="text-caption text-foreground @xl:text-meta min-w-0 font-medium">
                                <FormattedMessage id={`ai-chat.capabilities.entities.${id}`} />
                            </span>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
};

export default AiChatCapabilities;
